import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  useUpdateAdminUser,
  type AdminUser,
  type Role,
} from "@/queries/adminUsers";

import { classifyApiError } from "./errors";

interface EditUserFormProps {
  user: AdminUser;
  isSelf: boolean;
}

export function EditUserForm({ user, isSelf }: EditUserFormProps) {
  const { t } = useTranslation("admin-users");
  const update = useUpdateAdminUser(user.id);

  const [displayName, setDisplayName] = useState(user.display_name ?? "");
  const [role, setRole] = useState<Role>(user.role);
  const [isActive, setIsActive] = useState(user.is_active);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const userRadioId = useId();
  const adminRadioId = useId();
  const activeId = useId();
  const displayNameId = useId();

  useEffect(() => {
    setDisplayName(user.display_name ?? "");
    setRole(user.role);
    setIsActive(user.is_active);
  }, [user]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorKey(null);
    setSaved(false);

    const trimmedName = displayName.trim();
    update.mutate(
      {
        display_name: trimmedName === "" ? null : trimmedName,
        role,
        is_active: isActive,
      },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => {
          const bucket = classifyApiError(err);
          if (bucket === "demoteSelf") setErrorKey("demoteSelf");
          else if (bucket === "deactivateSelf") setErrorKey("deactivateSelf");
          else if (bucket === "lastAdmin") setErrorKey("lastAdmin");
          else setErrorKey("generic");
        },
      },
    );
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-2">
        <Label htmlFor={displayNameId}>{t("edit.displayNameLabel")}</Label>
        <Input
          id={displayNameId}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t("edit.displayNamePlaceholder")}
          maxLength={120}
          disabled={update.isPending}
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium leading-none">{t("edit.roleLabel")}</span>
        <RadioGroup
          value={role}
          onValueChange={(v) => setRole(v as Role)}
          disabled={update.isPending}
          className="grid grid-cols-2 gap-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="user" id={userRadioId} disabled={isSelf && user.role === "admin"} />
            <Label htmlFor={userRadioId} className="cursor-pointer text-sm">
              {t("create.roleUser")}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="admin" id={adminRadioId} />
            <Label htmlFor={adminRadioId} className="cursor-pointer text-sm">
              {t("create.roleAdmin")}
            </Label>
          </div>
        </RadioGroup>
        {isSelf && user.role === "admin" ? (
          <p className="text-xs text-muted-foreground">{t("edit.errors.demoteSelf")}</p>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <input
          id={activeId}
          type="checkbox"
          checked={isActive}
          disabled={update.isPending || isSelf}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border border-input"
        />
        <Label htmlFor={activeId} className="cursor-pointer text-sm">
          {t("edit.activeLabel")}
        </Label>
        {isSelf ? (
          <span className="text-xs text-muted-foreground">
            {t("edit.errors.deactivateSelf")}
          </span>
        ) : null}
      </div>

      {errorKey ? (
        <Alert variant="destructive">
          <AlertDescription>{t(`edit.errors.${errorKey}`)}</AlertDescription>
        </Alert>
      ) : null}
      {saved ? (
        <Alert>
          <AlertDescription>{t("edit.saved")}</AlertDescription>
        </Alert>
      ) : null}

      <div>
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? t("edit.submitting") : t("edit.submit")}
        </Button>
      </div>
    </form>
  );
}
